import React from 'react'
import { connect } from 'react-redux'
import { isString, compact } from 'lodash'
import { Table, FormGroup, FormControl, ControlLabel } from 'react-bootstrap'
import { Link } from 'react-router'

import Button from '../../components/Button.jsx'
import TripStatus from '../components/TripStatusSelect'
import EncargadoField from "../components/EncargadoField"
import { printMoney } from '../../../lib/Tools.js'
import { getText } from '../../lib/text.js'
import TagsList from '../../tags/TagsList'
import TripNotesDisplay from '../TripNotesDisplay'
import TripTasksDisplay from '../../tasks/TripTasksDisplay'
import Circle from '../../components/Circle'
import { getNextTaskPerTrip, getTasksCountPerTrip } from '../../selectors'

import { arrangeTripsList, processTripsList } from './arrange'

/**
 * per-tab settings
 */

// #TODO dedupe tab-configuration
const tabsWithUrgency = new Set(["nuevos", "propuesta", "negociando", "contrato"])
const tabsWithBill = new Set(["cerrados"])

const realerName = tv => {
  if (!tv.officialName && !tv.leaderName) return null

  return compact([tv.officialName, tv.leaderName]).join(", ")
}

const _starStrings = ["☆ ☆ ☆ ", "⭐️ ☆ ☆ ", "⭐️ ⭐️ ☆ ", "⭐️ ⭐️ ⭐️ "]
const Stars = ({ stars, style, placeholder }) => (
  <span style={style}>{_starStrings[stars] || placeholder || ""}</span>
)

/**
 *
 */

type TripsTableProps = {
  trips: Object[],
  tab: number,
  updateStatus: Function,
  updateComment: Function,
  updateTags: Function,
  onEdit: Function,
  onDelete: Function,
  openModal: Function,
  onSort: string => void,
  sortColumn: string,
  sortDirection: string,
  getPendingTasksCount: string => number
}

class TripsTable extends React.PureComponent {
  constructor(props) {
    super(props)
    this.state = { encargado: null }
  }

  render () {
    const {trips, Trips, Routes, Activities, Cities,
      tab, updateStatus, updateTags,
      onSort, sortColumn, sortDirection, getPendingTasksCount, tags,
      nextTasksPerUser, taskCounts, onTagClick, activeUserNames} = this.props

    const tripsData = processTripsList(trips, Routes, Activities, Cities, getPendingTasksCount)

    // there is no urgency in the later tabs; spot clients have no arrival
    const includeUrgency = tabsWithUrgency.has(tab)
    const includeBill = tabsWithBill.has(tab)
  
    let _sortColumn = sortColumn
    if ((_sortColumn == "arrival" || _sortColumn == "bill") && includeUrgency) _sortColumn = "urgency"
    if ((_sortColumn == "urgency" || _sortColumn == "bill") && (!includeUrgency && !includeBill)) _sortColumn = "arrival"
    if ((_sortColumn == "urgency" || _sortColumn == "arrival") && includeBill) _sortColumn = "bill"
  
    const sortedTripsData = arrangeTripsList(tripsData, _sortColumn, sortDirection).filter(t => (t.encargado == this.state.encargado) || !this.state.encargado)

      return (
        <div>
          <FormGroup>
            <ControlLabel>Ordenar por:</ControlLabel>
            <FormControl
              componentClass="select"
              value={_sortColumn}
              onChange={evt => onSort(evt.target.value)}
              style={{ maxWidth: 200, display: "inline-block", marginLeft: 15, marginRight: 15 }}
            >
              {
                [
                  { text: getText("Estado"),   sortable: true, sortKey: "status" },
                  { text: getText("Urgente"),  sortable: true, sortKey: "urgency" },
                  { text: getText("Clase"),    sortable: true, sortKey: "stars" },
                  { text: getText("Total"),    sortable: true, sortKey: "price" },
                  { text: getText("Factura"),  sortable: true, sortKey: "bill" },
                  { text: getText("Fechas"),   sortable: true, sortKey: "arrival" },
                  { text: getText("Creación"), sortable: true, sortKey: "creation" },
                ]
                  .map(({ text, sortKey }) => (
                    <option value={sortKey} key={sortKey}>{text}</option>
                  ))
              }
            </FormControl>
            <Button
              glyph="sort"
              onClick={() => onSort(_sortColumn)}
            >
              Desc/Asc
            </Button>
            <EncargadoField
              style={{ marginLeft: 30 }}
              onChange={evt => this.setState({encargado: evt.target.value})}
              onClick={() => this.setState({encargado: this.props.loginData.username})}
              value={this.state.encargado || ""}
              usernames={Array.from(activeUserNames)}
            />
          </FormGroup>
            
          <Table bordered striped>
            <thead
              style={{ height: 35, position: "sticky", top: 40, background: "white", zIndex: 20 }}
            >
              <tr>
                {includeBill && <th>{getText("Factura")}</th>}
                <th>{getText("")}</th>
                <th>{getText("Cliente")}</th>
                <th>{`🗨  ${getText("Mensajes")}`}</th>
                <th>{`☑️  ${getText("Próxima tarea")}`}</th>
              </tr>
            </thead>
            <tbody>
              {
                sortedTripsData.map(tripValues => (
                  isString(tripValues)
                    ? (<tr><td colSpan="666">{tripValues}</td></tr>)
                    : (
                      <tr key={tripValues._id}>
                        {/* factura */}
                        {
                          includeBill && (
                            <td
                              style={{ width: 40 }}
                            >
                              {
                                tripValues.receiptNumber
                                  ? (
                                    <div>
                                      <div style={{ marginBottom: 5 }}>
                                        {tripValues.receiptNumber}/{tripValues.receiptYear}
                                      </div>
                                      <div style={{ fontSize: 12 }}>
                                        {tripValues.receiptDate}
                                      </div>
                                    </div>
                                  )
                                  : ""
                              }
                            </td>
                          )
                        }

                        <td style={{ width: 36 }}>
                          {
                            includeUrgency && (
                              <Circle
                                percentage={tripValues.elapsedP}
                                text={`${tripValues.remainingTime}d`}
                                size="xsmall"
                                color={(tripValues.elapsedP <= 10 && "green") || (tripValues.elapsedP <= 20 && "orange") || "red" }
                              />
                            )
                          }
                          <div>
                            {
                              tripValues.encargado.charAt(0).toUpperCase() + tripValues.encargado.slice(1)
                            }
                          </div>
                        </td>

                        <td style={{ width: 350 }}>
                          <div>
                            <Link
                              to={`/private/trip/${tripValues._id}`}
                              style={{ width: 150, fontWeight: "bold", fontSize: 16 }}
                            >
                              {tripValues.realName || tripValues.name || "[Cliente sin nombre]"}
                            </Link>
                            <span style={{ marginRight: 10, marginLeft: 10 }}>-</span>
                            {
                              tripValues.paid > 0
                                ? (
                                  <span>
                                    <span style={{ fontSize: "0.8em" }}>{printMoney(tripValues.paid)} / </span>
                                    <span
                                      style={{
                                        color: tripValues.price.finalE > 5000 ? "green" : "black",
                                        fontWeight: tripValues.price.finalE  >= 5000 ? "bold" : "regular"
                                      }}
                                    >
                                      {printMoney(tripValues.price.finalE)}€
                                    </span>
                                  </span>
                                )
                                : (
                                  <span
                                    style={{
                                      color: tripValues.price.finalE > 5000 ? "green" : "black",
                                      fontWeight: tripValues.price.finalE  >= 5000 ? "bold" : "regular"
                                    }}
                                  >
                                    {printMoney(tripValues.price.finalE)}€
                                  </span>
                                )
                            }
                          </div>
                          {
                            realerName(tripValues) && (
                              <div style={{ fontSize: "0.9em" }}>
                                {realerName(tripValues)}
                              </div>
                            )
                          }
                          <div>
                            {tripValues.people}
                            <span style={{ marginLeft: 15 }}>{tripValues.dates}</span>
                            <TripStatus
                              style={{ display: "inline-block", maxWidth: 130, margin: 0, marginLeft: 15 }}
                              status={tripValues.status}
                              onChange={x => updateStatus(tripValues._id, x)}
                              includeLabel={false}
                              extraThin
                              shorter
                            />
                          </div>
                          <div>
                            <Stars style={{ float: "left" }} stars={tripValues.stars} />
                            <TagsList
                              allTags={tags}
                              selectedTags={tripValues.tags}
                              onChange={newSelection => updateTags(tripValues._id, newSelection)}
                              tagType="trips"
                              onClick={tagId => onTagClick(tagId)}
                            />
                          </div>
                        </td>

                        <td style={{ width: 500 }}>
                          <TripNotesDisplay trip={Trips[tripValues._id]} />
                        </td>

                        <td>
                          <TripTasksDisplay
                            trip={Trips[tripValues._id]}
                            nextTasksPerUser={nextTasksPerUser[tripValues._id]}
                            numberOfTasks={taskCounts[tripValues._id]}
                          />
                        </td>
                      </tr>
                    )
                ))
              }
            </tbody>
          </Table>
        </div>
      )
  }
}
const mapStateToProps = state => ({
  nextTasksPerUser: getNextTaskPerTrip(state),
  taskCounts: getTasksCountPerTrip(state),
  loginData: state.loginData,
})

export default connect(mapStateToProps)(TripsTable)
